import React, { useState, useMemo, useEffect } from 'react';
import { Card, Table, message, Typography, Select, Button, Upload, Form, Space, Spin, Progress, Tabs } from 'antd';
import { UploadOutlined, PlusOutlined, EyeOutlined, CloudUploadOutlined } from '@ant-design/icons';
import { RcFile } from 'antd/es/upload';
import { LocationSelector } from './LocationSelector';
import { api, apiMethods, Country, City } from '../services/api';
import dayjs from '../config/dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

interface PrayerTime {
    date: string;
    fajr: string;
    sunrise: string;
    dhuhr: string;
    asr: string;
    maghrib: string;
    isha: string;
}

const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

export const PrayerTimes: React.FC = () => {
    // Common state
    const [selectedCountry, setSelectedCountry] = useState('');
    const [selectedCity, setSelectedCity] = useState('');
    const [selectedLocationId, setSelectedLocationId] = useState('');
    const [selectedLocation, setSelectedLocation] = useState<{ country: string; city: string } | null>(null);
    
    // View state
    const [loading, setLoading] = useState(false);
    const [prayerTimes, setPrayerTimes] = useState<PrayerTime[]>([]);
    const [selectedMonth, setSelectedMonth] = useState(dayjs().month());
    const [timezone, setTimezone] = useState<string>('UTC');
    
    // Upload state
    const [isCreatingNew, setIsCreatingNew] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [creatingLocation, setCreatingLocation] = useState(false);
    const [countries, setCountries] = useState<Country[]>([]);
    const [cities, setCities] = useState<City[]>([]);
    const [loadingCountries, setLoadingCountries] = useState(false);
    const [loadingCities, setLoadingCities] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [citySearchText, setCitySearchText] = useState<string>('');
    const [form] = Form.useForm();

    const currentDate = dayjs().format('DD-MM');

    useEffect(() => {
        loadCountries();
    }, []);

    const loadCountries = async () => {
        try {
            setLoadingCountries(true);
            const data = await apiMethods.getCountries();
            data.sort((a, b) => a.name.common.localeCompare(b.name.common));
            setCountries(data);
        } catch (error) {
            console.error('Error loading countries:', error);
            message.error('Failed to load countries');
        } finally {
            setLoadingCountries(false);
        }
    };

    const loadCities = async () => {
        setCities([]);
        setCitySearchText('');
        form.setFieldValue('city', undefined);
    };

    const handleCitySearch = async (value: string) => {
        setCitySearchText(value);
        if (!form.getFieldValue('country')) return;

        try {
            setLoadingCities(true);
            setLoadingProgress(0);
            setCities([]);

            const selectedCountry = countries.find(c => c.name.common === form.getFieldValue('country'));
            if (!selectedCountry) return;

            const cities = await apiMethods.getCities(selectedCountry.cca2, value);
            setCities(cities);
            setLoadingProgress(100);
        } catch (error) {
            console.error('Error searching cities:', error);
            message.error('Failed to search cities');
        } finally {
            setLoadingCities(false);
        }
    };

    const getTimezoneFromCity = async (wikiDataId: string) => {
        try {
            const cityData = await apiMethods.getCityDetails(wikiDataId);
            if (cityData.timezone) {
                return cityData.timezone;
            }
            throw new Error('Timezone not found in city data');
        } catch (error) {
            console.error('Error fetching timezone:', error);
            message.warning('Could not determine timezone, using UTC');
            return 'UTC';
        }
    };

    const formatTime = (time: string, date: string) => {
        const [day, month] = date.split('-');
        const year = dayjs().year();
        const fullDate = `${year}-${month}-${day}`;
        const dateTime = dayjs.tz(`${fullDate} ${time}`, 'YYYY-MM-DD HH:mm', timezone);
        return dateTime.format('hh:mm A');
    };

    const filteredPrayerTimes = useMemo(() => {
        if (!prayerTimes.length) return [];
        
        return prayerTimes.filter(time => {
            const month = parseInt(time.date.split('-')[1]) - 1;
            return month === selectedMonth;
        }).sort((a, b) => {
            const dayA = parseInt(a.date.split('-')[0]);
            const dayB = parseInt(b.date.split('-')[0]);
            return dayA - dayB;
        });
    }, [prayerTimes, selectedMonth]);

    const handleLocationSelected = async (country: string, city: string, locationId: string, wikiDataId: string) => {
        setSelectedCountry(country);
        setSelectedCity(city);
        setSelectedLocationId(locationId);
        setSelectedLocation({ country, city });
        
        try {
            setLoading(true);
            
            const cityDetails = await apiMethods.getCityDetails(wikiDataId);
            
            if (!cityDetails.latitude || !cityDetails.longitude) {
                throw new Error('Could not get city coordinates');
            }

            const [prayerTimesData, locationTimezone] = await Promise.all([
                apiMethods.listPrayerTimes(locationId),
                (await getTimezoneFromCity(wikiDataId)).replace('__', '/')
            ]);

            setPrayerTimes(prayerTimesData);
            setTimezone(locationTimezone);
            setSelectedMonth(dayjs().month());
        } catch (error) {
            console.error('Error loading prayer times:', error);
            message.error('Failed to load prayer times');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateLocation = async (values: { country: string; city: string }) => {
        try {
            setCreatingLocation(true);
            
            const selectedCity = cities.find(c => c.name === values.city);
            if (!selectedCity) {
                message.error('Selected city not found');
                return;
            }

            const cityDetails = await apiMethods.getCityDetails(selectedCity.wikiDataId);

            const response = await api.post('/locations', {
                _id: '', // Temporary field to satisfy validation, will be generated by backend
                country: values.country,
                city: values.city,
                timezone: cityDetails?.timezone || 'UTC'
            });

            message.success('Location created successfully');
            setSelectedCountry(values.country);
            setSelectedCity(values.city);
            setSelectedLocationId(response.data._id);
            setSelectedLocation({ country: values.country, city: values.city });
            setIsCreatingNew(false);
        } catch (error: any) {
            console.error('Error creating location:', error);
            if (error.response?.status === 422) {
                const detail = error.response?.data?.detail;
                if (typeof detail === 'string') {
                    message.error(`Validation error: ${detail}`);
                } else if (Array.isArray(detail)) {
                    message.error(`Validation errors: ${detail.map(d => d.msg).join(', ')}`);
                } else {
                    message.error('Validation error occurred');
                }
            } else if (error.response?.data?.detail) {
                message.error(`Failed to create location: ${error.response.data.detail}`);
            } else {
                message.error('Failed to create location');
            }
        } finally {
            setCreatingLocation(false);
        }
    };

    const handleUpload = async (file: RcFile) => {
        if (!selectedCountry || !selectedCity || !selectedLocationId) {
            message.error('Please select or create a location first');
            return false;
        }

        const isValidFileType = file.type === 'text/csv' || 
            file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
            file.type === 'application/vnd.ms-excel';
        
        if (!isValidFileType) {
            message.error('You can only upload CSV or Excel files!');
            return false;
        }

        const isLessThan10MB = file.size / 1024 / 1024 < 10;
        if (!isLessThan10MB) {
            message.error('File must be smaller than 10MB!');
            return false;
        }

        try {
            setUploading(true);
            const formData = new FormData();
            formData.append('file', file);

            await api.post(`/prayer-times/upload/${selectedLocationId}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            message.success('Prayer times uploaded successfully');
            
            // Refresh the prayer times data if we're viewing the same location
            if (selectedLocation && selectedLocation.country === selectedCountry && selectedLocation.city === selectedCity) {
                // Reload prayer times data
                try {
                    const prayerTimesData = await apiMethods.listPrayerTimes(selectedLocationId);
                    setPrayerTimes(prayerTimesData);
                } catch (error) {
                    console.error('Error reloading prayer times:', error);
                }
            }
            
            return true;
        } catch (error: any) {
            console.error('Upload error:', error);
            if (error.response?.status === 404) {
                message.error('Location not found. Please make sure the location exists before uploading prayer times.');
            } else if (error.response?.status === 405) {
                message.error('Upload method not allowed. Please check the API endpoint.');
            } else if (error.response?.status === 422) {
                const detail = error.response?.data?.detail;
                if (typeof detail === 'string') {
                    message.error(`Invalid file format: ${detail}`);
                } else if (Array.isArray(detail)) {
                    message.error(`Validation error: ${detail.map(d => d.msg).join(', ')}`);
                } else {
                    message.error('Invalid file format. Please check the file structure and try again.');
                }
            } else if (error.response?.data?.detail) {
                message.error(`Failed to upload: ${error.response.data.detail}`);
            } else {
                message.error('Failed to upload prayer times. Please try again.');
            }
            return false;
        } finally {
            setUploading(false);
        }
    };

    const columns = [
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            width: 150,
            fixed: 'left' as const,
            render: (date: string) => {
                const [day, month] = date.split('-').map(Number);
                return dayjs().month(month - 1).date(day).format('ddd, DD MMM');
            }
        },
        {
            title: 'Fajr',
            dataIndex: 'fajr',
            key: 'fajr',
            width: 100,
            render: (time: string, record: PrayerTime) => formatTime(time, record.date)
        },
        {
            title: 'Sunrise',
            dataIndex: 'sunrise',
            key: 'sunrise',
            width: 100,
            render: (time: string, record: PrayerTime) => formatTime(time, record.date)
        },
        {
            title: 'Dhuhr',
            dataIndex: 'dhuhr',
            key: 'dhuhr',
            width: 100,
            render: (time: string, record: PrayerTime) => formatTime(time, record.date)
        },
        {
            title: 'Asr',
            dataIndex: 'asr',
            key: 'asr',
            width: 100,
            render: (time: string, record: PrayerTime) => formatTime(time, record.date)
        },
        {
            title: 'Maghrib',
            dataIndex: 'maghrib',
            key: 'maghrib',
            width: 100,
            render: (time: string, record: PrayerTime) => formatTime(time, record.date)
        },
        {
            title: 'Isha',
            dataIndex: 'isha',
            key: 'isha',
            width: 100,
            render: (time: string, record: PrayerTime) => formatTime(time, record.date)
        },
    ];

    const viewTabContent = (
        <div>
            <div style={{ marginBottom: 24 }}>
                <LocationSelector onLocationSelected={handleLocationSelected} />
            </div>

            {selectedLocation && (
                <div style={{ marginBottom: 16 }}>
                    <Title level={4}>
                        Prayer Times for {selectedLocation.city}, {selectedLocation.country}
                    </Title>
                    <Select
                        value={selectedMonth}
                        onChange={setSelectedMonth}
                        style={{ width: 200, marginBottom: 16 }}
                    >
                        {MONTHS.map((month, index) => (
                            <Option key={index} value={index}>
                                {month}
                            </Option>
                        ))}
                    </Select>
                </div>
            )}

            <Table
                dataSource={filteredPrayerTimes}
                columns={columns}
                loading={loading}
                scroll={{ x: 800 }}
                pagination={false}
                rowKey="date"
                rowClassName={(record) => {
                    return record.date === currentDate ? 'highlight-row' : '';
                }}
                className="prayer-times-table"
            />

            <style>
                {`
                    .prayer-times-table .highlight-row td {
                        background-color: #e6f7ff !important;
                    }
                    .prayer-times-table .highlight-row:hover td {
                        background-color: #bae7ff !important;
                    }
                `}
            </style>
        </div>
    );

    const uploadTabContent = (
        <div>
            {isCreatingNew ? (
                <div style={{ marginBottom: 24 }}>
                    <Title level={4}>Add New Location</Title>
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleCreateLocation}
                    >
                        <Form.Item
                            name="country"
                            label="Country"
                            rules={[{ required: true, message: 'Please select a country' }]}
                        >
                            <Select
                                showSearch
                                placeholder="Select a country"
                                loading={loadingCountries}
                                onChange={loadCities}
                                filterOption={(input, option) =>
                                    (option?.children as unknown as string)
                                        .toLowerCase()
                                        .includes(input.toLowerCase())
                                }
                            >
                                {countries.map(country => (
                                    <Option key={country.cca2} value={country.name.common}>
                                        {country.name.common}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item
                            name="city"
                            label="City"
                            rules={[{ required: true, message: 'Please select a city' }]}
                            extra={
                                loadingCities ? (
                                    <div style={{ marginTop: 8 }}>
                                        <Progress percent={loadingProgress} size="small" />
                                        <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                                            Searching cities...
                                        </Text>
                                    </div>
                                ) : (
                                    "Type at least 2 characters to search for a major city (population > 100,000)"
                                )
                            }
                        >
                            <Select
                                showSearch
                                placeholder="Type at least 2 characters to search for a major city"
                                loading={loadingCities}
                                disabled={!form.getFieldValue('country')}
                                onSearch={handleCitySearch}
                                filterOption={false}
                                notFoundContent={
                                    citySearchText.length < 2 ? 
                                        "Please type at least 2 characters to search" : 
                                        loadingCities ? 
                                            <Spin size="small" /> : 
                                            "No cities found"
                                }
                            >
                                {cities.map(city => (
                                    <Option key={city.id} value={city.name}>
                                        {city.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Space>
                            <Button type="primary" htmlType="submit" loading={creatingLocation}>
                                Create Location
                            </Button>
                            <Button onClick={() => setIsCreatingNew(false)}>
                                Cancel
                            </Button>
                        </Space>
                    </Form>
                </div>
            ) : (
                <div style={{ marginBottom: 24 }}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <LocationSelector onLocationSelected={handleLocationSelected} />
                        <Button 
                            type="link" 
                            icon={<PlusOutlined />}
                            onClick={() => setIsCreatingNew(true)}
                        >
                            Add New Location
                        </Button>
                    </Space>
                </div>
            )}

            {(selectedCountry && selectedCity) && (
                <div style={{ marginTop: 24 }}>
                    <Title level={4}>Upload Prayer Times</Title>
                    <Text>Selected Location: {selectedCountry} - {selectedCity}</Text>
                    
                    <div style={{ marginTop: 16 }}>
                        <Upload
                            accept=".csv,.xlsx,.xls"
                            beforeUpload={handleUpload}
                            showUploadList={false}
                            maxCount={1}
                        >
                            <Button 
                                icon={<UploadOutlined />} 
                                loading={uploading}
                            >
                                Select File to Upload
                            </Button>
                        </Upload>

                        <div style={{ marginTop: 16 }}>
                            <Text type="secondary">
                                Supported formats: CSV, Excel (xlsx, xls)
                                <br />
                                File size limit: 10MB
                                <br />
                                Expected columns: date, fajr, sunrise, dhuhr, asr, maghrib, isha
                            </Text>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <Card style={{ maxWidth: 1200, margin: '0 auto' }}>
            <Title level={3}>Prayer Times Management</Title>
            
            <Tabs
                defaultActiveKey="view"
                items={[
                    {
                        key: 'view',
                        label: (
                            <span>
                                <EyeOutlined />
                                View Times
                            </span>
                        ),
                        children: viewTabContent,
                    },
                    {
                        key: 'upload',
                        label: (
                            <span>
                                <CloudUploadOutlined />
                                Upload Times
                            </span>
                        ),
                        children: uploadTabContent,
                        disabled: false, // You can add auth check here if needed
                    },
                ]}
            />

            <div style={{ 
                marginTop: 24,
                borderTop: '1px solid #f0f0f0',
                paddingTop: 16,
                textAlign: 'center',
                color: 'rgba(0, 0, 0, 0.45)',
                fontSize: '14px'
            }}>
                True knowledge of when each prayer time begins relies on observation; any information provided here is for reference.
            </div>
        </Card>
    );
}; 